import { Helmet } from "react-helmet-async";
import { useContext } from "react";
import { LangContext } from "../App";
import { motion } from "framer-motion";
import { textVariants } from "../components/textVariants";
import { Box, Container, Card, CardContent, Grid } from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";

const Competences = () => {
  const language = useContext(LangContext);
  const techSkills_pl = [
    "Projektowanie i tworzenie aplikacji webowych SPA z wykorzystaniem React.js.",
    "Budowa responsywnych interfejsów użytkownika z użyciem Material UI, Bootstrap i CSS.",
    "Tworzenie backendów oraz interfejsów REST API w PHP (Slim Framework).",
    "Projektowanie struktur baz danych oraz optymalizacja zapytań SQL (MySQL, MSSQL).",
    "Integracja aplikacji z zewnętrznymi usługami i API.",
    "Automatyzacja procesów biznesowych i przepływu danych.",
    "Projektowanie architektury aplikacji oraz organizacja logiki biznesowej.",
    "Wdrażanie i utrzymanie aplikacji na serwerach Linux i VPS.",
    "Konfiguracja środowisk Docker i procesów wdrożeniowych.",
    "Administracja domenami, DNS, pocztą elektroniczną i certyfikatami SSL.",
    "Zabezpieczanie aplikacji, kontrola dostępu i tworzenie kopii zapasowych.",
    "Monitoring, utrzymanie i rozwój systemów produkcyjnych.",
  ];
  const techSkills_en = [
    "Development of modern Single Page Applications (SPA) using React.js.",
    "Design and implementation of responsive user interfaces with Bootstrap, Material UI, and CSS3.",
    "Backend development and REST API implementation in PHP.",
    "Design, optimization, and maintenance of MySQL and Microsoft SQL Server databases.",
    "Integration with third-party systems and business process automation.",
    "Web application architecture design and data flow planning.",
    "Deployment and maintenance of applications on VPS servers and Linux environments.",
    "Docker configuration and deployment process automation.",
    "Domain, DNS, email service, and SSL certificate configuration.",
    "Application security, data protection, and backup strategies.",
    "Application performance optimization and technical SEO.",
    "Monitoring, maintenance, and continuous improvement of production systems.",
  ];
  const managementSkills_pl = [
    "Kierowanie zespołami IT oraz koordynacja pracy programistów i administratorów.",
    "Planowanie i realizacja projektów informatycznych – od analizy potrzeb biznesowych po wdrożenie i rozwój systemów.",
    "Współpraca z zarządem oraz klientami w zakresie definiowania wymagań i planowania rozwoju produktów.",
    "Optymalizacja procesów biznesowych poprzez projektowanie i wdrażanie dedykowanych rozwiązań informatycznych.",
    "Zarządzanie budżetem IT oraz kontrola kosztów utrzymania infrastruktury i systemów.",
    "Nadzór nad bezpieczeństwem, ciągłością działania oraz rozwojem środowiska IT.",
    "Zarządzanie pełnym cyklem życia aplikacji: analiza, projektowanie, wdrożenie, utrzymanie i rozwój.",
    "Tworzenie oraz rozwój własnych produktów internetowych i aplikacji wykorzystywanych komercyjnie.",
  ];
  const managementSkills_en = [
    "Managing the IT department and coordinating the work of software developers and system administrators.",
    "Planning and delivering IT projects from business requirements analysis through implementation and ongoing system development.",
    "Collaborating with executive management and clients to define business requirements and product development strategies.",
    "Optimizing business processes by designing and implementing tailored IT solutions.",
    "Managing IT budgets and optimizing infrastructure and system maintenance costs.",
    "Ensuring IT security, business continuity, and the continuous development of the IT environment.",
    "Managing the full application lifecycle, including analysis, design, implementation, maintenance, and continuous improvement.",
    "Creating and developing commercial web applications and proprietary digital products.",
  ];
  const styles = {
    card: {
      background: "#ffffff",
      borderRadius: "4px",
      padding: "36px",
      boxShadow: "0 10px 30px -10px rgba(0,0,0,0.04)",
      border: "1px solid #e2e8f0",
      height: "100%",
      boxSizing: "border-box",
    },
    cardHeaderGradient: {
      background: "linear-gradient(135deg, #1E3A5F 0%, #274C77 100%)",
      padding: "10px",
      borderRadius: "22px",
      textAlign: "left",
      marginBottom: "20px",
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
    },
    cardTitleGradient: {
      color: "#fff",
      fontSize: "20px",
      margin: 0,
      display: "flex",
      alignItems: "center",
      gap: "20px",
      paddingLeft: "10px",
    },
  };
  const cardContentStyle = {
    p: 4,
    pt: 0,
    flexGrow: 1,
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    width: "1340px",
    maxWidth: "100%",
  };
  const cardStyle = {
    height: "100%",
    borderRadius: "24px",
    boxShadow: "0 10px 30px -10px rgba(0,0,0,0.04)",
    border: "1px solid #e2e8f0",
    display: "flex",
    flexDirection: "column",
    width: "100%",
  };
  const listStyle = {
    display: "flex",
    flexDirection: "column",
    gap: 2,
    m: 0,
    p: 0,
    listStyle: "none",
  };
  const renderList = (skills) => (
    <Box component="ul" sx={listStyle}>
      {skills.map((skill, index) => (
        <Box
          component="li"
          key={`${index}-${skill}`}
          sx={{
            display: "flex",
            alignItems: "flex-start",
            gap: 1.5,
            color: "#475569",
            fontSize: "15px",
            lineHeight: 1.6,
          }}
        >
          <CheckCircleOutlineIcon
            sx={{
              color: "#274C77",
              fontSize: 19,
              flexShrink: 0,
              mt: "2px",
            }}
          />
          <Box component="span">{skill}</Box>
        </Box>
      ))}
    </Box>
  );

  return (
    <>
      <Helmet>
        <title>
          Kompetencje IT – React, PHP, SQL i zarządzanie IT | Michał Jaskulski
        </title>

        <meta
          name="description"
          content="Poznaj kompetencje Michała Jaskulskiego: React, PHP, MySQL, MSSQL, REST API, administracja Linux, infrastruktura IT oraz zarządzanie projektami informatycznymi."
        />

        <link
          rel="canonical"
          href="https://michaljaskulski.pl/react-developer"
        />
      </Helmet>

      <Box className="page-container">
        <motion.div
          initial="hidden"
          animate="visible"
          exit="hidden"
          variants={textVariants}
        >
          <Container maxWidth="lg">
            {/* TYTUŁ I OPIS */}
            <Box sx={{ textAlign: "center", mb: { xs: 4, md: 6 } }}>
              <h1>
                {language === "pl"
                  ? "Kompetencje techniczne i doświadczenie w IT"
                  : "Technical Skills and IT Management Experience"}
              </h1>

              {/*
                <h2>
                  {language === "en"
                    ? "I combine IT management experience with hands-on software development. I design and develop web applications, database systems and integrations that improve everyday business operations. I understand both business requirements and the technical aspects of project delivery."
                    : "Łączę doświadczenie w zarządzaniu IT z praktycznym tworzeniem oprogramowania. Projektuję i rozwijam aplikacje webowe, systemy bazodanowe oraz integracje, które usprawniają codzienną pracę firm. Rozumiem zarówno potrzeby biznesowe, jak i techniczne aspekty realizacji projektów."}
                </h2>
                */}
            </Box>

            {/* KARTY KOMPETENCJI */}
            <Grid container spacing={4} alignItems="stretch">
              {/* KARTA 1 */}
              <Grid item xs={12} md={6} sx={{ display: "flex" }}>
                <Card sx={cardStyle}>
                  <Box
                    sx={{
                      background:
                        "linear-gradient(135deg, #1E3A5F 0%, #274C77 100%)",
                      p: 2,
                      borderRadius: "20px 20px 0 0",
                      mb: 3,
                      maxWidth: "130%",
                    }}
                  >
                    <h2 style={styles.cardTitleGradient}>
                      {language === "pl"
                        ? "Umiejętności techniczne"
                        : "Technical Skills"}
                    </h2>
                  </Box>

                  <CardContent sx={cardContentStyle}>
                    {renderList(
                      language === "pl" ? techSkills_pl : techSkills_en,
                    )}
                  </CardContent>
                </Card>
              </Grid>

              {/* KARTA 2 */}
              <Grid item xs={12} md={6} sx={{ display: "flex" }}>
                <Card sx={cardStyle}>
                  <Box
                    sx={{
                      background:
                        "linear-gradient(135deg, #1E3A5F 0%, #274C77 100%)",
                      p: 2,
                      borderRadius: "20px 20px 0 0",
                      mb: 3,
                      maxWidth: "130%",
                    }}
                  >
                    <h2 style={styles.cardTitleGradient}>
                      {language === "pl"
                        ? "Zarządzanie IT i projektami"
                        : "IT Management & Project Delivery"}
                    </h2>
                  </Box>

                  <CardContent sx={cardContentStyle}>
                    {renderList(
                      language === "pl"
                        ? managementSkills_pl
                        : managementSkills_en,
                    )}
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Container>
        </motion.div>
      </Box>
    </>
  );
};

export default Competences;
